import React, { useCallback, useMemo, useState } from 'react';
import type { CommunityComment } from '../types';
import type { AsyncStatus } from '../redux/communitySlice';
import './CommunityCommentList.css';

interface CommunityCommentListProps {
  comments: CommunityComment[];
  status: AsyncStatus;
  error: string | null;
  onReload?: () => void;
  onSubmit: (content: string, parentCommentId?: string | null) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onToggleLike: (commentId: string, hasLiked: boolean) => Promise<void>;
  likePending: Record<string, boolean>;
  createStatus: AsyncStatus;
  createError: string | null;
  currentUserId?: number | null;
  postOwnerId?: number | null;
}

const CommunityCommentList: React.FC<CommunityCommentListProps> = ({
  comments,
  status,
  error,
  onReload,
  onSubmit,
  onDelete,
  onToggleLike,
  likePending,
  createStatus,
  createError,
  currentUserId,
  postOwnerId,
}) => {
  const [draft, setDraft] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<CommunityComment | null>(null);
  const submitting = createStatus === 'loading';

  const canManage = useCallback(
    (comment: CommunityComment) => {
      if (!currentUserId) {
        return false;
      }
      if (comment.user?.id === currentUserId) {
        return true;
      }
      if (postOwnerId && postOwnerId === currentUserId) {
        return true;
      }
      return false;
    },
    [currentUserId, postOwnerId],
  );

  const commentsByParent = useMemo(() => {
    const buckets = new Map<string | null, CommunityComment[]>();
    comments.forEach((comment) => {
      const parentId = comment.parent_comment_id ?? null;
      const list = buckets.get(parentId) ?? [];
      list.push(comment);
      buckets.set(parentId, list);
    });
    buckets.forEach((list) => {
      list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
    return buckets;
  }, [comments]);

  const rootComments = useMemo(() => commentsByParent.get(null) ?? [], [commentsByParent]);

  const handleReply = (comment: CommunityComment) => {
    setReplyTarget(comment);
    const authorName = comment.user?.name || comment.user?.username || '';
    if (authorName && !draft) {
      setDraft(`@${authorName} `);
    }
  };

  const renderComment = useCallback(
    (comment: CommunityComment, depth: number = 0) => {
      const likeCount = comment.stats?.likes ?? 0;
      const replyCount = comment.stats?.replies ?? 0;
      const hasLiked = comment.viewer?.has_liked ?? false;
      const pending = likePending[comment.comment_id] ?? false;
      const childList = commentsByParent.get(comment.comment_id) ?? [];
      return (
        <li
          key={comment.comment_id}
          className={`community-comments__item${depth > 0 ? ' community-comments__item--nested' : ''}`}
        >
          <div className="community-comments__avatar">
            {comment.user?.avatar_url ? (
              <img src={comment.user.avatar_url} alt={comment.user?.name || comment.user?.username || '用户'} />
            ) : (
              <span>{(comment.user?.name || comment.user?.username || '友').slice(0, 1).toUpperCase()}</span>
            )}
          </div>
          <div className="community-comments__body">
            <div className="community-comments__meta">
              <span className="community-comments__author">{comment.user?.name || comment.user?.username || '匿名用户'}</span>
              <span className="community-comments__time">{new Date(comment.created_at).toLocaleString()}</span>
              {replyCount > 0 && <span className="community-comments__counter">回复 {replyCount}</span>}
              {canManage(comment) && (
                <button
                  type="button"
                  className="community-comments__delete"
                  onClick={() => {
                    void onDelete(comment.comment_id);
                  }}
                >
                  删除
                </button>
              )}
            </div>
            <div className="community-comments__content">{comment.content}</div>
            <div className="community-comments__toolbar">
              <button
                type="button"
                className={`community-comments__action community-comments__action--like${hasLiked ? ' community-comments__action--active' : ''}`}
                onClick={() => {
                  if (!pending) {
                    void onToggleLike(comment.comment_id, hasLiked);
                  }
                }}
                disabled={pending}
              >
                {pending ? '处理中...' : `赞同 ${likeCount}`}
              </button>
              <button
                type="button"
                className="community-comments__action"
                onClick={() => handleReply(comment)}
              >
                回复
              </button>
            </div>
            {childList.length > 0 && (
              <ul className="community-comments__list community-comments__list--nested">
                {childList.map((child) => renderComment(child, depth + 1))}
              </ul>
            )}
          </div>
        </li>
      );
    },
    [canManage, commentsByParent, handleReply, likePending, onDelete, onToggleLike],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) {
      setSubmitError('请输入评论内容');
      return;
    }
    setSubmitError(null);
    try {
      await onSubmit(content, replyTarget?.comment_id ?? null);
      setDraft('');
      setReplyTarget(null);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '发表评论失败');
    }
  };

  const renderStatus = () => {
    if (status === 'loading') {
      return <div className="community-comments__status">评论加载中...</div>;
    }
    if (status === 'failed' && error) {
      return (
        <div className="community-comments__status community-comments__status--error">
          <span>{error}</span>
          {onReload && (
            <button type="button" onClick={onReload}>重试</button>
          )}
        </div>
      );
    }
    if (!rootComments.length) {
      return <div className="community-comments__status community-comments__status--empty">暂无评论</div>;
    }
    return null;
  };

  return (
    <div className="community-comments">
      <form className="community-comments__composer" onSubmit={handleSubmit}>
        <textarea
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (submitError) {
              setSubmitError(null);
            }
          }}
          placeholder="分享你的看法..."
          disabled={submitting}
        />
        {replyTarget && (
          <div className="community-comments__replying">
            回复 {replyTarget.user?.name || replyTarget.user?.username || '用户'}
            <button type="button" onClick={() => setReplyTarget(null)}>
              取消
            </button>
          </div>
        )}
        {(submitError || createError) && (
          <div className="community-comments__composer-error">{submitError || createError}</div>
        )}
        <div className="community-comments__actions">
          <button type="submit" disabled={submitting}>
            {submitting ? '发送中...' : '发表评论'}
          </button>
        </div>
      </form>

      {renderStatus()}

      {rootComments.length > 0 && (
        <ul className="community-comments__list">
          {rootComments.map((comment) => renderComment(comment, 0))}
        </ul>
      )}
    </div>
  );
};

export default CommunityCommentList;
